import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

export default function AuditTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    table: '',
    operation: '',
    userEmail: '',
  });
  const [pageSize, setPageSize] = useState(50);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', pageSize);

      if (filters.table) params.append('table', filters.table);
      if (filters.operation) params.append('operation', filters.operation);
      if (filters.userEmail) params.append('user_email', filters.userEmail);

      const data = await apiCall(`/api/audit-logs?${params.toString()}`);
      setLogs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatJson = (jsonStr) => {
    if (!jsonStr) return null;
    try {
      return JSON.parse(jsonStr);
    } catch {
      return jsonStr;
    }
  };

  const getOperationColor = (operation) => {
    switch (operation) {
      case 'CREATE':
        return 'bg-green-100 text-green-800';
      case 'UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading)
    return <div className="flex items-center justify-center py-12">Loading audit logs...</div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters */}
      <div className="card space-y-4 p-3 sm:p-4">
        <h3 className="text-base sm:text-lg font-bold font-headline">Filters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label htmlFor="filter-table" className="block text-xs sm:text-sm font-medium mb-2">
              Table
            </label>
            <select
              id="filter-table"
              value={filters.table}
              onChange={(e) => setFilters({ ...filters, table: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All tables</option>
              <option value="tasks">Tasks</option>
              <option value="files">Files</option>
              <option value="file_tags">File Tags</option>
            </select>
          </div>

          <div>
            <label htmlFor="filter-operation" className="block text-xs sm:text-sm font-medium mb-2">
              Operation
            </label>
            <select
              id="filter-operation"
              value={filters.operation}
              onChange={(e) => setFilters({ ...filters, operation: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All operations</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
            </select>
          </div>

          <div>
            <label htmlFor="filter-email" className="block text-xs sm:text-sm font-medium mb-2">
              User Email
            </label>
            <input
              id="filter-email"
              type="text"
              value={filters.userEmail}
              onChange={(e) => setFilters({ ...filters, userEmail: e.target.value })}
              placeholder="Filter by email..."
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface placeholder-on-surface-variant focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="page-size" className="block text-xs sm:text-sm font-medium mb-2">
              Results per page
            </label>
            <select
              id="page-size"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Audit Log Entries */}
      <div className="space-y-3">
        {logs.length === 0 ? (
          <div className="card text-center py-8 text-on-surface-variant">
            <p>No audit logs found.</p>
          </div>
        ) : (
          logs.map((log) => (
            <div key={log.id} className="card p-3 sm:p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex-1">
                  <div className="flex flex-wrap gap-2 items-center mb-2">
                    <span className={`badge text-xs font-semibold px-2 py-1 rounded ${getOperationColor(log.operation)}`}>
                      {log.operation}
                    </span>
                    <span className="text-xs sm:text-sm font-medium text-on-surface">{log.table_name}</span>
                  </div>
                  <p className="text-xs text-on-surface-variant">{log.details}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-on-surface-variant">
                <div>
                  <strong>User:</strong> {log.user_email}
                </div>
                <div>
                  <strong>Time:</strong> {formatDate(log.timestamp)}
                </div>
                <div className="sm:col-span-2">
                  <strong>Entity ID:</strong> <code className="text-xs bg-surface-container px-2 py-1 rounded">{log.entity_id}</code>
                </div>
              </div>

              {(log.before_data || log.after_data) && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-semibold text-on-surface hover:text-primary">
                    View changes
                  </summary>
                  <div className="mt-2 space-y-2 pl-3 border-l-2 border-outline-variant">
                    {log.before_data && (
                      <div>
                        <strong>Before:</strong>
                        <pre className="mt-1 bg-surface-container p-2 rounded text-xs overflow-x-auto">
                          {JSON.stringify(formatJson(log.before_data), null, 2)}
                        </pre>
                      </div>
                    )}
                    {log.after_data && (
                      <div>
                        <strong>After:</strong>
                        <pre className="mt-1 bg-surface-container p-2 rounded text-xs overflow-x-auto">
                          {JSON.stringify(formatJson(log.after_data), null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
